import React from 'react';
import { X } from 'lucide-react';
import { clsx } from 'clsx';

interface LeftoverFabricModalProps {
    fabrics: Array<{name: string, qty: number, uom: string, leftover: number}>;
    onUpdate: (fabrics: Array<{name: string, qty: number, uom: string, leftover: number}>) => void;
    onConfirm: () => void;
    onCancel: () => void;
}

export const LeftoverFabricModal: React.FC<LeftoverFabricModalProps> = ({ fabrics, onUpdate, onConfirm, onCancel }) => {
    const handleLeftoverChange = (index: number, value: string) => {
        const updated = [...fabrics];
        updated[index].leftover = parseFloat(value) || 0;
        onUpdate(updated);
    };

    return (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4">
            <div className="bg-white rounded-3xl shadow-2xl max-w-md w-full max-h-[80vh] overflow-hidden flex flex-col">
                {/* Header */}
                <div className="p-6 border-b border-slate-100 flex items-center justify-between">
                    <h2 className="text-xl font-bold text-slate-800">Leftover Fabric</h2>
                    <button 
                        onClick={onCancel}
                        className="w-8 h-8 rounded-lg bg-slate-100 flex items-center justify-center text-slate-500 hover:bg-slate-200 transition-colors"
                    >
                        <X size={18} />
                    </button>
                </div>

                {/* Content */}
                <div className="flex-1 overflow-y-auto p-6 space-y-4">
                    <p className="text-sm text-slate-500 mb-4">
                        Enter the leftover quantity for each fabric after cutting.
                    </p>

                    {fabrics.map((fabric, index) => (
                        <div key={index} className="bg-slate-50 p-4 rounded-xl space-y-2">
                            <div className="flex items-center justify-between">
                                <p className="text-sm font-bold text-slate-700">{fabric.name}</p>
                                <p className="text-xs text-slate-400">Issued: {fabric.qty} {fabric.uom}</p>
                            </div>
                            
                            <div className="flex items-center gap-2">
                                <label className="text-xs text-slate-500 font-medium">Leftover:</label>
                                <input
                                    type="number"
                                    step="0.1"
                                    min="0"
                                    max={fabric.qty}
                                    value={fabric.leftover || ''}
                                    onChange={(e) => handleLeftoverChange(index, e.target.value)}
                                    className="flex-1 px-3 py-2 bg-white border border-slate-200 rounded-lg text-sm font-bold text-slate-700 focus:outline-none focus:ring-2 focus:ring-blue-500/20"
                                    placeholder="0.0"
                                />
                                <span className="text-sm text-slate-500">{fabric.uom}</span>
                            </div>

                            <div className="text-xs text-slate-400">
                                Used: {(fabric.qty - fabric.leftover).toFixed(2)} {fabric.uom}
                            </div>
                        </div>
                    ))}
                </div>

                {/* Footer */}
                <div className="p-6 border-t border-slate-100 flex gap-3">
                    <button
                        onClick={onCancel}
                        className="flex-1 py-3 rounded-xl text-sm font-bold text-slate-600 bg-slate-100 hover:bg-slate-200 transition-colors"
                    >
                        Cancel
                    </button>
                    <button
                        onClick={onConfirm}
                        className="flex-1 py-3 rounded-xl text-sm font-bold text-white bg-blue-600 hover:bg-blue-700 active:scale-95 transition-all shadow-md shadow-blue-600/20"
                    >
                        Confirm & Complete
                    </button>
                </div>
            </div>
        </div>
    );
};
